import React, { useEffect, useState } from "react";
import AdminLayout from "src/layout/admin";
import GenericDataLayout from "src/components/custom/GenericDataLayout";
import GenericTable from "src/components/custom/GenericTable";
import Modal from "src/components/modal";
import Button from "src/components/custom/Button";
import {
  getMethodApiCall,
  postMethodApiCall,
  putMethodApiCall,
} from "src/services/api";
import { toast } from "react-toastify";
import {
  emptyCreateForm,
  LABEL_CLASS,
  TABLE_COLUMNS,
  validateCreateStudentForm,
  validateUpdateStudentForm,
  buildCreateStudentPayload,
  buildUpdateStudentPayload,
  emptyEditForm,
  mapTraineeDetailToForm,
} from "src/utils/mcc/studentHelper";
import StudentFormFields from "src/components/mcc/StudentFormFields";
import StudentFilterSelect from "src/components/mcc/StudentFilterSelect";

const DetailRow = ({ label, value }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-1 sm:gap-3 border-b border-gray-100 py-2.5 last:border-0">
    <span className="text-xs font-semibold text-primary">{label}</span>
    <span className="text-sm text-gray-800 break-words">{value ?? "—"}</span>
  </div>
);

const Student = () => {
  const [tableRows, setTableRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
  });

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState(() => emptyCreateForm());
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailViewMode, setDetailViewMode] = useState("view");
  const [traineeDetailRaw, setTraineeDetailRaw] = useState(null);
  const [editForm, setEditForm] = useState(() => emptyEditForm());
  const [detailLoading, setDetailLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editItinameOptions, setEditItinameOptions] = useState([]);
  const [editTradenameOptions, setEditTradenameOptions] = useState([]);
  const [editIndustrynameOptions, setEditIndustrynameOptions] = useState([]);

  const [districtOptions, setDistrictOptions] = useState([]);
  const [itinameOptions, setItinameOptions] = useState([]);
  const [tradenameOptions, setTradenameOptions] = useState([]);
  const [createIndustrynameOptions, setCreateIndustrynameOptions] = useState([]);

  const [filterRegistration, setFilterRegistration] = useState("");
  const [filterUsername, setFilterUsername] = useState("");

  const loadStudents = async (
    page = 1,
    limit = 10,
    reg = filterRegistration,
    user = filterUsername
  ) => {
    setListLoading(true);
    try {
      const params = { page, limit };
      const r = String(reg ?? "").trim();
      const u = String(user ?? "").trim();
      if (r) params.registration_id = r;
      if (u) params.username = u;

      const res = await getMethodApiCall("/v1/api/mcc/students", params);
      if (res?.status === 200 && res?.data?.success) {
        setTableRows(res.data.data || []);
        const p = res.data.pagination || {};
        setPagination((prev) => ({
          ...prev,
          page: p.page ?? page,
          limit: p.limit ?? limit,
          totalRecords: p.total ?? 0,
        }));
      } else {
        setTableRows([]);
        toast.error(res?.data?.message || "Failed to load students");
      }
    } catch (e) {
      setTableRows([]);
      toast.error("Failed to load students");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadStudents(1, pagination.limit, "", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchDistricts = async () => {
      const res = await getMethodApiCall("/v1/api/trainee/get_district_picklist");
      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.district)
          .map((item) => ({
            label: item.district,
            value: item.district,
          }));
        setDistrictOptions(options);
      }
    };
    fetchDistricts();
  }, []);

  useEffect(() => {
    const fetchItisByDistrict = async () => {
      if (!createForm.district) {
        setItinameOptions([]);
        setTradenameOptions([]);
        setCreateForm((prev) => ({
          ...prev,
          itiname: "",
          tradename: "",
          industryname: "",
        }));
        return;
      }

      const res = await getMethodApiCall(
        `/v1/api/trainee/get_iti_picklist/${encodeURIComponent(createForm.district)}`
      );

      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.iti_name)
          .map((item) => ({
            label: item.iti_name,
            value: item.id,
          }));
        setItinameOptions(options);
      } else {
        setItinameOptions([]);
        setTradenameOptions([]);
        setCreateForm((prev) => ({
          ...prev,
          itiname: "",
          tradename: "",
          industryname: "",
        }));
      }
    };

    if (addModalOpen) {
      fetchItisByDistrict();
    }
  }, [createForm.district, addModalOpen]);

  useEffect(() => {
    const fetchTradesByIti = async () => {
      if (!createForm.itiname) {
        setTradenameOptions([]);
        setCreateIndustrynameOptions([]);
        setCreateForm((prev) => ({
          ...prev,
          tradename: "",
          industryname: "",
        }));
        return;
      }

      const res = await getMethodApiCall(
        `/v1/api/trainee/trades_by_iti/${createForm.itiname}`
      );

      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.trade_name)
          .map((item) => ({
            label: item.trade_name,
            value: item.id,
          }));
        setTradenameOptions(options);
      } else {
        setTradenameOptions([]);
        setCreateIndustrynameOptions([]);
        setCreateForm((prev) => ({
          ...prev,
          tradename: "",
          industryname: "",
        }));
      }
    };

    if (addModalOpen) {
      fetchTradesByIti();
    }
  }, [createForm.itiname, addModalOpen]);

  useEffect(() => {
    const fetchIndustriesByTradeAndITI = async () => {
      if (!addModalOpen || !createForm.itiname || !createForm.tradename) {
        setCreateIndustrynameOptions([]);
        return;
      }

      const res = await getMethodApiCall(
        `/v1/api/trainee/industries_by_trade_and_iti/${encodeURIComponent(
          createForm.tradename
        )}/${encodeURIComponent(createForm.itiname)}`
      );

      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.industry_name)
          .map((item) => ({
            label: item.industry_name,
            value: item.id,
          }));
        setCreateIndustrynameOptions(options);
      } else {
        setCreateIndustrynameOptions([]);
        setCreateForm((prev) => ({ ...prev, industryname: "" }));
      }
    };

    fetchIndustriesByTradeAndITI();
  }, [createForm.itiname, createForm.tradename, addModalOpen]);

  /** Edit modal: ITI / trade picklists */
  useEffect(() => {
    const run = async () => {
      if (!detailModalOpen || !editForm.district) {
        setEditItinameOptions([]);
        return;
      }
      const res = await getMethodApiCall(
        `/v1/api/trainee/get_iti_picklist/${encodeURIComponent(editForm.district)}`
      );
      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.iti_name)
          .map((item) => ({
            label: item.iti_name,
            value: item.id,
          }));
        setEditItinameOptions(options);
      } else {
        setEditItinameOptions([]);
      }
    };
    run();
  }, [editForm.district, detailModalOpen]);

  useEffect(() => {
    const run = async () => {
      if (!detailModalOpen || !editForm.itiname) {
        setEditTradenameOptions([]);
        setEditIndustrynameOptions([]);
        return;
      }
      const res = await getMethodApiCall(
        `/v1/api/trainee/trades_by_iti/${editForm.itiname}`
      );
      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.trade_name)
          .map((item) => ({
            label: item.trade_name,
            value: item.id,
          }));
        setEditTradenameOptions(options);
      } else {
        setEditTradenameOptions([]);
        setEditIndustrynameOptions([]);
      }
    };
    run();
  }, [editForm.itiname, detailModalOpen]);

  useEffect(() => {
    const run = async () => {
      if (!detailModalOpen || !editForm.itiname || !editForm.tradename) {
        setEditIndustrynameOptions([]);
        return;
      }

      const res = await getMethodApiCall(
        `/v1/api/trainee/industries_by_trade_and_iti/${encodeURIComponent(
          editForm.tradename
        )}/${encodeURIComponent(editForm.itiname)}`
      );

      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.industry_name)
          .map((item) => ({
            label: item.industry_name,
            value: item.id,
          }));
        setEditIndustrynameOptions(options);
      } else {
        setEditIndustrynameOptions([]);
        setEditForm((prev) => ({ ...prev, industryname: "" }));
      }
    };
    run();
  }, [editForm.itiname, editForm.tradename, detailModalOpen]);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    loadStudents(newPage, pagination.limit, filterRegistration, filterUsername);
  };

  const handleLimitChange = (newLimit) => {
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
    loadStudents(1, newLimit, filterRegistration, filterUsername);
  };

  const handleResetStudentFilters = () => {
    setFilterRegistration("");
    setFilterUsername("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    loadStudents(1, pagination.limit, "", "");
  };

  const clearCreateForm = () => {
    setCreateForm(emptyCreateForm());
    setItinameOptions([]);
    setTradenameOptions([]);
    setCreateIndustrynameOptions([]);
  };

  const openAddModal = () => {
    clearCreateForm();
    setAddModalOpen(true);
  };

  const closeAddModal = () => setAddModalOpen(false);

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setDetailViewMode("view");
    setTraineeDetailRaw(null);
    setEditForm(emptyEditForm());
    setEditItinameOptions([]);
    setEditTradenameOptions([]);
    setEditIndustrynameOptions([]);
  };

  const reloadDetailFromServer = async (id) => {
    const res = await getMethodApiCall(`/v1/api/mcc/students/${id}`);
    if (res?.status === 200 && res?.data?.success && res.data.data) {
      setTraineeDetailRaw(res.data.data);
      setEditForm(mapTraineeDetailToForm(res.data.data));
      return true;
    }
    toast.error(res?.data?.message || "Failed to load student");
    return false;
  };

  const handleRowClick = async (row) => {
    if (!row?.id) return;
    setDetailLoading(true);
    setDetailModalOpen(true);
    setDetailViewMode("view");
    try {
      await reloadDetailFromServer(row.id);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreateStudent = async () => {
    const v = validateCreateStudentForm(createForm);
    if (!v.ok) {
      toast.error(v.message);
      return;
    }

    setCreateSubmitting(true);
    try {
      const payload = buildCreateStudentPayload(createForm);
      const res = await postMethodApiCall("/v1/api/mcc/students", payload);

      if (res?.status === 201 && res?.data?.success) {
        toast.success(res.data.message || "Student created successfully");
        clearCreateForm();
        setAddModalOpen(false);
        await loadStudents(1, pagination.limit, filterRegistration, filterUsername);
      } else {
        toast.error(
          res?.data?.message ||
            res?.data?.error ||
            "Failed to create student"
        );
      }
    } catch {
      toast.error("Failed to create student");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleUpdateStudent = async () => {
    const v = validateUpdateStudentForm(editForm);
    if (!v.ok) {
      toast.error(v.message);
      return;
    }
    if (!editForm.id) {
      toast.error("Missing student id");
      return;
    }

    setEditSubmitting(true);
    try {
      const payload = buildUpdateStudentPayload(editForm);
      const res = await putMethodApiCall(
        `/v1/api/mcc/students/${editForm.id}`,
        payload
      );

      if (res?.status === 200 && res?.data?.success) {
        toast.success(res.data.message || "Student updated successfully");
        await reloadDetailFromServer(editForm.id);
        setDetailViewMode("view");
        await loadStudents(
          pagination.page,
          pagination.limit,
          filterRegistration,
          filterUsername
        );
      } else {
        toast.error(
          res?.data?.message || res?.data?.error || "Failed to update student"
        );
      }
    } catch {
      toast.error("Failed to update student");
    } finally {
      setEditSubmitting(false);
    }
  };

  const cancelEditBackToView = async () => {
    if (!editForm.id) {
      closeDetailModal();
      return;
    }
    setDetailLoading(true);
    try {
      await reloadDetailFromServer(editForm.id);
      setDetailViewMode("view");
    } finally {
      setDetailLoading(false);
    }
  };

  const actions = [
    {
      label: "Add",
      onClick: (e) => {
        e.preventDefault?.();
        openAddModal();
      },
    },
  ];

  const detailTitle =
    detailViewMode === "view" ? "Student details" : "Edit student";

  const studentFilterConfig = [
    {
      type: "custom",
      render: () => (
        <StudentFilterSelect
          label="Registration ID"
          field="registration_id"
          value={filterRegistration}
          onChange={(v) => {
            setFilterRegistration(v);
            setPagination((p) => ({ ...p, page: 1 }));
            loadStudents(1, pagination.limit, v, filterUsername);
          }}
          placeholder="Search registration ID…"
        />
      ),
    },
    {
      type: "custom",
      render: () => (
        <StudentFilterSelect
          label="Username"
          field="username"
          value={filterUsername}
          onChange={(v) => {
            setFilterUsername(v);
            setPagination((p) => ({ ...p, page: 1 }));
            loadStudents(1, pagination.limit, filterRegistration, v);
          }}
          placeholder="Search username…"
        />
      ),
    },
  ];

  return (
    <AdminLayout>
      <GenericDataLayout
        title="Student"
        subtitle=""
        actions={actions}
        filters={studentFilterConfig}
        onResetFilters={handleResetStudentFilters}
        isLoading={listLoading}
        pagination={{
          page: pagination.page,
          limit: pagination.limit,
          totalRecords: pagination.totalRecords,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      >
        <GenericTable
          columns={TABLE_COLUMNS}
          data={tableRows}
          pagination={pagination}
          onRowClick={handleRowClick}
        />
      </GenericDataLayout>

      <Modal isOpen={addModalOpen} onClose={closeAddModal} title="Add Student">
        <StudentFormFields
          form={createForm}
          setForm={setCreateForm}
          districtOptions={districtOptions}
          itinameOptions={itinameOptions}
          tradenameOptions={tradenameOptions}
          industrynameOptions={createIndustrynameOptions}
          courseAffiliationGroupName="course_affiliation_mcc_add"
        />
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={clearCreateForm}
            disabled={createSubmitting}
            className="py-2 px-6 min-w-[8rem] rounded-md border-2 border-primary text-primary font-semibold shadow-sm hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition ease-in duration-200 disabled:opacity-50"
          >
            Clear
          </button>
          <Button
            title={createSubmitting ? "Creating..." : "Create"}
            disabled={createSubmitting}
            onClick={handleCreateStudent}
          />
        </div>
      </Modal>

      <Modal isOpen={detailModalOpen} onClose={closeDetailModal} title={detailTitle}>
        {detailLoading && (
          <p className="text-sm text-gray-500 py-4">Loading…</p>
        )}
        {!detailLoading && traineeDetailRaw && detailViewMode === "view" && (
          <>
            <div className="space-y-0 pr-1">
              <DetailRow label="Registration ID" value={traineeDetailRaw.registration_id} />
              <DetailRow label="Username" value={traineeDetailRaw.username} />
              <DetailRow label="Name" value={traineeDetailRaw.name} />
              <DetailRow label="Phone" value={traineeDetailRaw.phone_number} />
              <DetailRow label="Date of Birth" value={traineeDetailRaw.date_of_birth} />
              <DetailRow label="Father's Name" value={traineeDetailRaw.father_name} />
              <DetailRow label="Mother's Name" value={traineeDetailRaw.mother_name} />
              <DetailRow label="Course Affiliation" value={traineeDetailRaw.course_affiliation_} />
              <DetailRow label="City of ITI" value={traineeDetailRaw.city_of_ITI} />
              <DetailRow label="District" value={traineeDetailRaw.district_of_ITI} />
              <DetailRow label="ITI" value={traineeDetailRaw.iti_name} />
              <DetailRow label="Trade" value={traineeDetailRaw.trade_name} />
              <DetailRow label="Industry" value={traineeDetailRaw.industry_name} />
              <DetailRow label="Batch of Trade" value={traineeDetailRaw.batch_of_trade} />
              <DetailRow label="Gender" value={traineeDetailRaw.gender} />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDetailModal}
                className="py-2 px-6 min-w-[8rem] rounded-md border-2 border-gray-300 text-gray-700 font-semibold shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition ease-in duration-200"
              >
                Cancel
              </button>
              <Button title="Edit" onClick={() => setDetailViewMode("edit")} />
            </div>
          </>
        )}

        {!detailLoading && traineeDetailRaw && detailViewMode === "edit" && (
          <>
            <StudentFormFields
              form={editForm}
              setForm={setEditForm}
              districtOptions={districtOptions}
              itinameOptions={editItinameOptions}
              tradenameOptions={editTradenameOptions}
              industrynameOptions={editIndustrynameOptions}
              courseAffiliationGroupName="course_affiliation_mcc_edit"
              showReadOnlyCredentials
            />
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={cancelEditBackToView}
                disabled={editSubmitting}
                className="py-2 px-6 min-w-[8rem] rounded-md border-2 border-gray-300 text-gray-700 font-semibold shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition ease-in duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <Button
                title={editSubmitting ? "Updating..." : "Update"}
                disabled={editSubmitting}
                onClick={handleUpdateStudent}
              />
            </div>
          </>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default Student;
